// EN/FR copy for Core Responsibilities (self-assessment, manager evaluation, monthly history).
// These views render their own language (data-cvi-i18n="managed").

const plural = (n, one, many) => (n === 1 ? one : many);

const COPY = {
    en: {
        locale: "en-CA",
        month: m => `Month ${m}`,
        months: "Months",
        period: "Period",
        periodCurrent: label => `${label} · current`,
        periodClosed: label => `${label} · closed`,
        loading: "Loading core responsibilities…",
        loadError: "Your core responsibilities could not be loaded.",
        retry: "Try again",
        noPeriod: "There is no active period. Core responsibilities open when a period is activated.",
        noQuestions: "No criteria are defined for this month.",
        notRecorded: "Not recorded",
        nameUnavailable: "Name not available",
        weight: w => `Weight ${w}%`,
        criterion: n => `Criterion ${n}`,
        self: {
            title: "Self-assessment",
            recordTitle: m => `Month ${m} record`,
            lockedIntro: "Submitted and locked. Your manager evaluates this month next; their ratings appear here once recorded.",
            recordIntro: "Read-only. Your self-assessment and your manager’s evaluation as recorded for this month.",
            intro: "Rate each criterion for the selected month. Your self-assessment informs the conversation with your manager; it has no direct weight in your CVI.",
            yourRating: "Your rating",
            comment: "Comment or supporting evidence",
            commentPlaceholder: "Optional · what supports this rating?",
            challenge: "Challenge or support need",
            saving: "Saving…", saved: "Saved", saveFailed: "Not saved",
            autosave: "Ratings and comments are saved as you go.",
            submit: m => `Submit month ${m}`, submitting: "Submitting…",
            submitHint: "Once submitted, this month is locked and your manager can evaluate it.",
            remainingHint: n => `Rate ${n} more ${plural(n, "criterion", "criteria")} to submit.`,
            confirm: m => `Submit your self-assessment for month ${m}? Submitted answers are locked.`,
            incomplete: "Rate every criterion for this month before submitting.",
            notRated: "Not rated",
        },
        manager: {
            title: "Manager evaluation",
            official: "Official rating · counts toward your CVI",
            byOn: (name, date) => [name, date].filter(Boolean).join(" · "),
            pending: "Not evaluated yet",
            comment: "Manager comment",
        },
        overview: {
            title: m => `Month ${m} overview`,
            assessed: "Criteria rated",
            assessedValue: (done, total) => `${done} of ${total}`,
            remaining: n => `${n} remaining`,
            complete: "All rated",
            status: "Self-assessment",
            due: "Submission deadline",
            submitted: "Submitted",
            evaluation: "Manager evaluation",
            editing: "Editing",
            editOpen: "Open · saved automatically",
            editLocked: "Locked · submitted",
            editClosed: "Read-only · period closed",
        },
        status: {
            not_started: "Not started", in_progress: "In progress", submitted: "Submitted", evaluated: "Evaluated",
            overdue: "Overdue", partial: "In progress", complete: "Completed", pending: "Not started", not_submitted: "Not submitted",
        },
        history: {
            title: "Monthly evaluation history",
            subtitle: "What has been recorded for each month of this period.",
            month: "Month", self: "Self-assessment", manager: "Manager evaluation", statusColumn: "Status", open: "View",
            submittedOn: d => `Submitted ${d}`,
            savedNotSubmitted: "Saved, not submitted",
            notStarted: "Not started",
            evaluatedBy: (name, d) => [name, d].filter(Boolean).join(" · "),
            partial: (done, total) => `${done} of ${total} rated`,
            awaiting: "Awaiting manager evaluation",
            result: {
                evaluated: "Evaluated", submitted: "Submitted", in_progress: "In progress", not_started: "Not started", overdue: "Overdue", not_submitted: "Not submitted",
            },
            log: "Activity log",
            showAll: n => `Show all ${n} entries`, showLess: "Show recent entries",
            logEvents: {
                SELF_ASSESSMENT_SUBMITTED: "Self-assessment submitted",
                MANAGER_EVALUATION_SAVED: "Manager rating recorded",
                MANAGER_EVALUATION_UPDATED: "Manager rating changed",
                other: "Update recorded",
            },
            by: name => `by ${name}`,
            noLog: "No detailed activity log was recorded for this period. The overview above reflects the ratings and dates saved with each assessment.",
            updatedNote: "The last change date is shown; earlier versions of a rating are not kept.",
        },
        review: {
            back: "Back",
            title: "Core Responsibilities review",
            subtitle: "Rate each criterion once the employee has submitted that month’s self-assessment. Your rating is the official evaluation.",
            score: "Core Responsibilities score (quarter to date)",
            answered: (done, total) => `${done} of ${total} criteria rated this quarter`,
            selfRef: "Employee self-assessment",
            selfRefHelp: "For reference · no weight in the CVI",
            selfMissing: "Self-assessment not submitted yet · rating opens once it is",
            yourRating: "Your rating",
            comment: "Comment for the employee",
            commentPlaceholder: "Optional · explain the rating",
            readOnlyClosed: "This period is closed. Evaluations are read-only.",
            readOnly: "You can view this evaluation but not change it.",
            noneSubmitted: "No monthly self-assessment has been submitted yet. Rating opens month by month as each one is submitted.",
            submittedMonths: list => `Self-assessment submitted for ${list}.`,
            waiting: "Waiting for the self-assessment",
            saving: "Saving…", saved: "Saved",
            saveFailed: "The rating could not be saved. Try again.",
            notRated: "Not rated",
            members: n => `${n} ${plural(n, "person", "people")}`,
            memberProgress: (done, total) => `${done} of ${total} criteria rated`,
            listIntro: "Select a person to rate their core responsibilities. Each month opens once that month’s self-assessment is submitted.",
            scoreShort: "Core Responsibilities",
            noMembers: "No one is assigned to you for this evaluation.",
            noQuestions: "No core responsibilities criteria are defined for this unit yet.",
            weightTotal: (n, w) => `${n} criteria · total weight ${w}%`,
            weightWarning: "The administrator must set the total weight to 100%.",
            monthCriteria: m => `Month ${m} criteria`,
            monthProgress: (done, total) => `${done} of ${total} rated`,
            quarterProgress: "Rated this quarter",
            criterionState: { rated: "Rated", ready: "Ready to rate", waiting: "Awaiting self-assessment", none: "No rating recorded" },
        },
        responsibility: {
            title: "Evaluation Responsibility",
            kicker: "Period-level criterion",
            intro: "How reliably this person completed the reviews, approvals and feedback that others depend on. Rated once for the whole period.",
            importance: "Default importance: Very High",
            effectiveWeightZero: "Effective weight: 0% · redistributed to the other criteria",
            state: {
                loading: "Loading evidence", unavailable: "Evidence unavailable", verification: "Verification pending",
                not_applicable: "Not applicable", exempt: "Exempt", pending: "Pending rating", evaluated: "Evaluated", stale: "Rating to confirm",
            },
            loading: "Loading responsibility evidence…",
            unavailable: "Responsibility evidence could not be loaded, so no rating can be recorded right now. This does not mean the criterion is not applicable.",
            verification: "Responsibilities are being tracked for this period, but not every evidence source has been verified yet. Manager rating opens once verification is complete; until then this criterion is not counted in the CVI.",
            verificationSources: n => `${n} evidence ${plural(n, "source", "sources")} awaiting verification.`,
            recordedWhileGated: (level, date) => `A rating (${level}) was recorded${date ? ` on ${date}` : ""}. It is not counted while verification is pending.`,
            notApplicable: "No qualifying evaluation responsibilities were assigned to this person this period.",
            exempt: "An administrator exempted this person’s evaluation responsibilities for this period.",
            pending: "Review the evidence, then record your rating. It is saved as soon as you select it.",
            evaluated: (name, date) => `Rated${name ? ` by ${name}` : ""}${date ? ` on ${date}` : ""}.`,
            stale: date => `The evidence changed after the rating recorded${date ? ` on ${date}` : ""}. Confirm or update the rating so it can be counted.`,
            cannotRate: "The evidence does not allow a rating for this period.",
            readOnlyClosed: "This period is closed. The rating is read-only.",
            readOnlyView: "Only the employee’s direct manager can record this rating.",
            metrics: {
                assigned: "Assigned workload", assignedHelp: n => `${n} ${plural(n, "responsibility", "responsibilities")} assigned`,
                completion: "On-time completion", completionHelp: (done, due) => `${done} of ${due} due on time`, completionNone: "Nothing due yet",
                overdue: "Overdue responsibilities", overdueHelp: "Open past their due date", lateHelp: n => `${n} completed late`,
            },
            yourRating: "Manager assessment",
            saving: "Saving…", saved: "Saved", saveFailed: "The rating could not be saved. Try again.",
            retry: "Try again",
        },
        challenge: {
            type: "Type of challenge", none: "No challenge",
            types: {
                EXTERNAL_DEPENDENCY: "External or cross-unit dependency", RESOURCE_LIMITATION: "Resource constraint", PROCESS_ISSUE: "Process issue",
                SYSTEM_TOOL: "System or tool", MANAGEMENT_DECISION: "Management decision", OTHER: "Other",
            },
            status: "Status", statuses: { none: "No challenge", open: "Unresolved", in_progress: "In progress", resolved: "Resolved" },
            description: "Challenge description", support: "Support needed", action: "Suggested action",
        },
    },
    fr: {
        locale: "fr-CA",
        month: m => `Mois ${m}`,
        months: "Mois",
        period: "Période",
        periodCurrent: label => `${label} · en cours`,
        periodClosed: label => `${label} · fermée`,
        loading: "Chargement des responsabilités principales…",
        loadError: "Vos responsabilités principales n’ont pas pu être chargées.",
        retry: "Réessayer",
        noPeriod: "Aucune période n’est active. Les responsabilités principales s’ouvrent à l’activation d’une période.",
        noQuestions: "Aucun critère n’est défini pour ce mois.",
        notRecorded: "Non consigné",
        nameUnavailable: "Nom non disponible",
        weight: w => `Pondération ${w}\u00a0%`,
        criterion: n => `Critère ${n}`,
        self: {
            title: "Autoévaluation",
            recordTitle: m => `Dossier du mois ${m}`,
            lockedIntro: "Soumis et verrouillé. Votre gestionnaire évalue maintenant ce mois; ses évaluations s’afficheront ici une fois consignées.",
            recordIntro: "Lecture seule. Votre autoévaluation et l’évaluation de votre gestionnaire telles qu’elles ont été consignées pour ce mois.",
            intro: "Évaluez chaque critère pour le mois sélectionné. Votre autoévaluation alimente l’échange avec votre gestionnaire; elle n’a pas de poids direct dans votre CVI.",
            yourRating: "Votre évaluation",
            comment: "Commentaire ou pièce justificative",
            commentPlaceholder: "Facultatif · qu’est-ce qui appuie cette évaluation?",
            challenge: "Défi ou besoin de soutien",
            saving: "Enregistrement…", saved: "Enregistré", saveFailed: "Non enregistré",
            autosave: "Les évaluations et les commentaires sont enregistrés au fur et à mesure.",
            submit: m => `Soumettre le mois ${m}`, submitting: "Soumission…",
            submitHint: "Une fois soumis, ce mois est verrouillé et votre gestionnaire peut l’évaluer.",
            remainingHint: n => `Évaluez encore ${n} ${plural(n, "critère", "critères")} pour soumettre.`,
            confirm: m => `Soumettre votre autoévaluation du mois ${m}? Les réponses soumises sont verrouillées.`,
            incomplete: "Évaluez chaque critère de ce mois avant de soumettre.",
            notRated: "Non évalué",
        },
        manager: {
            title: "Évaluation du gestionnaire",
            official: "Évaluation officielle · compte dans votre CVI",
            byOn: (name, date) => [name, date].filter(Boolean).join(" · "),
            pending: "Pas encore évalué",
            comment: "Commentaire du gestionnaire",
        },
        overview: {
            title: m => `Aperçu du mois ${m}`,
            assessed: "Critères évalués",
            assessedValue: (done, total) => `${done} sur ${total}`,
            remaining: n => `${n} ${plural(n, "restant", "restants")}`,
            complete: "Tous évalués",
            status: "Autoévaluation",
            due: "Date limite",
            submitted: "Soumis le",
            evaluation: "Évaluation du gestionnaire",
            editing: "Modification",
            editOpen: "Ouverte · enregistrement automatique",
            editLocked: "Verrouillée · soumis",
            editClosed: "Lecture seule · période fermée",
        },
        status: {
            not_started: "Non commencé", in_progress: "En cours", submitted: "Soumis", evaluated: "Évalué",
            overdue: "En retard", partial: "En cours", complete: "Terminée", pending: "Non commencée", not_submitted: "Non soumis",
        },
        history: {
            title: "Historique des évaluations mensuelles",
            subtitle: "Ce qui a été consigné pour chaque mois de cette période.",
            month: "Mois", self: "Autoévaluation", manager: "Évaluation du gestionnaire", statusColumn: "Statut", open: "Voir",
            submittedOn: d => `Soumis le ${d}`,
            savedNotSubmitted: "Enregistrée, non soumise",
            notStarted: "Non commencée",
            evaluatedBy: (name, d) => [name, d].filter(Boolean).join(" · "),
            partial: (done, total) => `${done} sur ${total} évalués`,
            awaiting: "En attente de l’évaluation du gestionnaire",
            result: {
                evaluated: "Évalué", submitted: "Soumis", in_progress: "En cours", not_started: "Non commencé", overdue: "En retard", not_submitted: "Non soumis",
            },
            log: "Journal d’activité",
            showAll: n => `Afficher les ${n} entrées`, showLess: "Afficher les entrées récentes",
            logEvents: {
                SELF_ASSESSMENT_SUBMITTED: "Autoévaluation soumise",
                MANAGER_EVALUATION_SAVED: "Évaluation du gestionnaire consignée",
                MANAGER_EVALUATION_UPDATED: "Évaluation du gestionnaire modifiée",
                other: "Mise à jour consignée",
            },
            by: name => `par ${name}`,
            noLog: "Aucun journal d’activité détaillé n’a été consigné pour cette période. L’aperçu ci-dessus reflète les évaluations et les dates enregistrées avec chaque évaluation.",
            updatedNote: "La date de la dernière modification est affichée; les versions antérieures d’une évaluation ne sont pas conservées.",
        },
        review: {
            back: "Retour",
            title: "Évaluation des responsabilités principales",
            subtitle: "Évaluez chaque critère une fois que la personne a soumis son autoévaluation du mois. Votre évaluation est l’évaluation officielle.",
            score: "Résultat des responsabilités principales (trimestre à ce jour)",
            answered: (done, total) => `${done} ${plural(done, "critère évalué", "critères évalués")} sur ${total} ce trimestre`,
            selfRef: "Autoévaluation de la personne",
            selfRefHelp: "À titre indicatif · aucun poids dans le CVI",
            selfMissing: "Autoévaluation non soumise · l’évaluation s’ouvre à la soumission",
            yourRating: "Votre évaluation",
            comment: "Commentaire pour la personne",
            commentPlaceholder: "Facultatif · expliquez l’évaluation",
            readOnlyClosed: "Cette période est fermée. Les évaluations sont en lecture seule.",
            readOnly: "Vous pouvez consulter cette évaluation, mais pas la modifier.",
            noneSubmitted: "Aucune autoévaluation mensuelle n’a encore été soumise. L’évaluation s’ouvre mois par mois, à chaque soumission.",
            submittedMonths: list => `Autoévaluation soumise pour ${list}.`,
            waiting: "En attente de l’autoévaluation",
            saving: "Enregistrement…", saved: "Enregistré",
            saveFailed: "L’évaluation n’a pas pu être enregistrée. Réessayez.",
            notRated: "Non évalué",
            members: n => `${n} ${plural(n, "personne", "personnes")}`,
            memberProgress: (done, total) => `${done} ${plural(done, "critère évalué", "critères évalués")} sur ${total}`,
            listIntro: "Sélectionnez une personne pour évaluer ses responsabilités principales. Chaque mois s’ouvre à la soumission de l’autoévaluation correspondante.",
            scoreShort: "Responsabilités principales",
            noMembers: "Personne ne vous est assigné pour cette évaluation.",
            noQuestions: "Aucun critère de responsabilités principales n’est encore défini pour cette unité.",
            weightTotal: (n, w) => `${n} critères · pondération totale ${w}\u00a0%`,
            weightWarning: "L’administrateur doit fixer la pondération totale à 100\u00a0%.",
            monthCriteria: m => `Critères du mois ${m}`,
            monthProgress: (done, total) => `${done} sur ${total} évalués`,
            quarterProgress: "Évalués ce trimestre",
            criterionState: { rated: "Évalué", ready: "Prêt à évaluer", waiting: "En attente de l’autoévaluation", none: "Aucune évaluation consignée" },
        },
        responsibility: {
            title: "Responsabilité en matière d’évaluation",
            kicker: "Critère de la période",
            intro: "La fiabilité avec laquelle cette personne a réalisé les revues, approbations et rétroactions dont d’autres dépendent. Évalué une seule fois pour toute la période.",
            importance: "Importance par défaut\u00a0: Très élevée",
            effectiveWeightZero: "Pondération effective\u00a0: 0\u00a0% · redistribuée aux autres critères",
            state: {
                loading: "Chargement des données", unavailable: "Données non disponibles", verification: "Vérification en cours",
                not_applicable: "Sans objet", exempt: "Exemptée", pending: "Évaluation à faire", evaluated: "Évaluée", stale: "Évaluation à confirmer",
            },
            loading: "Chargement des données de responsabilité…",
            unavailable: "Les données de responsabilité n’ont pas pu être chargées; aucune évaluation ne peut être consignée pour le moment. Cela ne signifie pas que le critère est sans objet.",
            verification: "Les responsabilités de cette période sont suivies, mais toutes les sources de données n’ont pas encore été vérifiées. L’évaluation du gestionnaire s’ouvrira une fois la vérification terminée; d’ici là, ce critère n’est pas pris en compte dans le CVI.",
            verificationSources: n => `${n} ${plural(n, "source de données en attente", "sources de données en attente")} de vérification.`,
            recordedWhileGated: (level, date) => `Une évaluation (${level}) a été consignée${date ? ` le ${date}` : ""}. Elle n’est pas prise en compte tant que la vérification est en cours.`,
            notApplicable: "Aucune responsabilité d’évaluation admissible n’a été attribuée à cette personne pour cette période.",
            exempt: "Un administrateur a exempté cette personne de ses responsabilités d’évaluation pour cette période.",
            pending: "Examinez les données, puis consignez votre évaluation. Elle est enregistrée dès que vous la sélectionnez.",
            evaluated: (name, date) => `Évaluée${name ? ` par ${name}` : ""}${date ? ` le ${date}` : ""}.`,
            stale: date => `Les données ont changé après l’évaluation consignée${date ? ` le ${date}` : ""}. Confirmez ou modifiez l’évaluation pour qu’elle soit prise en compte.`,
            cannotRate: "Les données ne permettent pas d’évaluation pour cette période.",
            readOnlyClosed: "Cette période est fermée. L’évaluation est en lecture seule.",
            readOnlyView: "Seul le gestionnaire immédiat de la personne peut consigner cette évaluation.",
            metrics: {
                assigned: "Charge attribuée", assignedHelp: n => `${n} ${plural(n, "responsabilité attribuée", "responsabilités attribuées")}`,
                completion: "Réalisation dans les délais", completionHelp: (done, due) => `${done} sur ${due} échues réalisées à temps`, completionNone: "Rien n’est encore échu",
                overdue: "Responsabilités en retard", overdueHelp: "Ouvertes après leur échéance", lateHelp: n => `${n} ${plural(n, "réalisée", "réalisées")} en retard`,
            },
            yourRating: "Évaluation du gestionnaire",
            saving: "Enregistrement…", saved: "Enregistré", saveFailed: "L’évaluation n’a pas pu être enregistrée. Réessayez.",
            retry: "Réessayer",
        },
        challenge: {
            type: "Type de défi", none: "Aucun défi",
            types: {
                EXTERNAL_DEPENDENCY: "Dépendance externe ou entre unités", RESOURCE_LIMITATION: "Contrainte de ressources", PROCESS_ISSUE: "Problème de processus",
                SYSTEM_TOOL: "Système ou outil", MANAGEMENT_DECISION: "Décision de la direction", OTHER: "Autre",
            },
            status: "Statut", statuses: { none: "Aucun défi", open: "Non résolu", in_progress: "En cours", resolved: "Résolu" },
            description: "Description du défi", support: "Soutien requis", action: "Action suggérée",
        },
    },
};

export function routineCopy(language) {
    return COPY[language === "fr" ? "fr" : "en"];
}
